import React, { useEffect, useRef, useState } from "react";
import DataTable from 'react-data-table-component-with-filter';
import { useNavigate } from 'react-router-dom';
import { FaThList, FaTh, FaSearch, FaArchive, FaCaretDown, FaCog, FaUpload } from 'react-icons/fa';
import './Patients.css';
import InternalHeader from '../InternalHeader';
import Sidebar from '../Sidebar';
import KanbanView from './KanbanView';
import CustomPagination from '../CustomPagination/CustomPagination';
import Filter from "../Filter/Filter";
import Actions from "../Actions/Actions";
import Papa from 'papaparse'; // Import PapaParse for CSV parsing
import { format } from 'date-fns';
import AccessControl from '../AccessControl';

const Patients = () => {
    const [patients, setPatients] = useState([]);
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [isKanbanView, setIsKanbanView] = useState(false);
    const [viewType, setViewType] = useState('table');
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewOption, setViewOption] = useState('all');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);  // Reference for the dropdown container
    const [selectedRows, setSelectedRows] = useState([]);
    const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
    const [selectedPatients, setSelectedPatients] = useState([]); // State for selected patients
    const [logs, setLogs] = useState([]);

    const logChange = (action, details) => {
        setLogs(prevLogs => [...prevLogs, { timestamp: new Date().toLocaleString(), action, details }]);
    };


    const handleSelectedRowsChange = ({ selectedRows }) => {
        setSelectedRows(selectedRows.map(row => row._id));
        setSelectedPatients(selectedRows); // Update selected patients
    };

    const handleDeleteSelected = async () => {
        if (selectedRows.length === 0) {
            alert("No patients selected for deletion!");
            return;
        }

        const confirmArchive = window.confirm('Are you sure you want to archive the selected patients?');
        if (!confirmArchive) {
            return;
        }

        try {
            // Send a request to update the isArchived field for selected patients
            const response = await fetch('https://api.hgcdental.clinic/api/patients/archive-multiple', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ patientIds: selectedRows }), // Send the IDs of selected patients
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'Failed to archive patients');
            }

            // Update the local state to reflect the changes
            setPatients((prevPatients) =>
                prevPatients.map((patient) =>
                    selectedRows.includes(patient._id) ? { ...patient, isArchived: true } : patient
                )
            );

            // Clear the selected rows
            setSelectedRows([]);
            setSelectedPatients([]);

            alert(result.message || 'Patients archived successfully');
        } catch (error) {
            console.error('Error archiving patients:', error);
            alert(error.message || 'An error occurred while archiving patients');
        }
    };

    const handleUnDeleteSelected = async () => {
        if (selectedRows.length === 0) {
            alert("No patients selected for unarchiving!");
            return;
        }

        const confirmUnarchive = window.confirm('Are you sure you want to unarchive the selected patients?');
        if (!confirmUnarchive) {
            return;
        }

        try {
            // Send a request to update the isArchived field for selected patients
            const response = await fetch('https://api.hgcdental.clinic/api/patients/unarchive-multiple', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ patientIds: selectedRows }), // Send the IDs of selected patients
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'Failed to unarchive patients');
            }

            // Update the local state to reflect the changes
            setPatients((prevPatients) =>
                prevPatients.map((patient) =>
                    selectedRows.includes(patient._id)
                        ? { ...patient, isArchived: false } // Set isArchived to false
                        : patient
                )
            );

            // Clear the selected rows
            setSelectedRows([]);
            setSelectedPatients([]);

            alert(result.message || 'Patients unarchived successfully');
        } catch (error) {
            console.error('Error unarchiving patients:', error);
            alert(error.message || 'An error occurred while unarchiving patients');
        }
    };

    const handleGenerateCSV = () => {
        handleExport(); // Call handleExport when generating CSV
    };

    const handleActionsToggle = () => {
        setIsActionsDropdownOpen(!isActionsDropdownOpen);
    };

    const totalRecords = patients.length;

    const toggleSidebar = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    useEffect(() => {
        const fetchPatients = async () => {
            const response = await fetch('https://api.hgcdental.clinic/api/patients');
            const json = await response.json();

            if (response.ok) {
                logChange('Fetched', `Loaded ${json.length} patients.`);
                setPatients(json);
            }
        };

        fetchPatients();
    }, []);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleRowsPerPageChange = (newRowsPerPage) => {
        setRowsPerPage(Number(newRowsPerPage));
        setCurrentPage(1);
    };

    const handleImport = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        Papa.parse(file, {
            complete: async (result) => {
                if (!result.data || result.data.length < 2) {
                    alert("Invalid or empty CSV file.");
                    return;
                }

                // Ensure the first row contains headers
                const headers = result.data[0];
                if (!Array.isArray(headers)) {
                    alert("CSV format error: Headers not recognized.");
                    return;
                }

                // Format CSV rows into objects
                const formattedData = result.data.slice(1).map(row => {
                    let patient = {};
                    headers.forEach((header, index) => {
                        if (row[index] !== undefined) {
                            patient[header.trim()] = row[index]?.trim();
                        }
                    });
                    return patient;
                });

                try {
                    const response = await fetch('https://api.hgcdental.clinic/api/patients/import', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(formattedData),
                    });

                    const result = await response.json();
                    if (!response.ok) throw new Error(result.message || 'Import failed');

                    alert(result.message);
                    setPatients((prev) => [...prev, ...result.insertedPatients]);
                } catch (error) {
                    console.error('Error importing:', error);
                    alert(error.message || 'Failed to import patients');
                }
            },
            header: false, // We will manually extract headers from the first row
            skipEmptyLines: true, // Ignore empty rows
        });
    };

    const handleExport = () => {
        if (!Array.isArray(selectedPatients) || selectedPatients.length === 0) {
            alert('No records selected for export!');
            return;
        }

        // Define headers for the CSV file
        const headers = [
            '_id', 'dateOfExam', 'chartNumber', 'name', 'sex', 'birthdate', 'ageYear', 'ageMonth',
            'guardianName', 'occupation', 'address', 'ContactInformation', 'email', 'chiefComplaint',
            'medicalDentalHistory', 'orthodonticHistory', 'currentTreatment', 'congenitalAbnormalities',
            'tmjProblems', 'oralHygiene', 'gingivalTissues', 'abnormalBleeding', 'asthma', 'cancer',
            'congenitalHeartDefect', 'convulsionsEpilepsy', 'diabetes', 'handicapsDisabilities',
            'hearingImpairment', 'heartMurmur', 'hemophilia', 'hepatitis', 'hivAids', 'kidneyLiverProblems',
            'rheumaticScarletFever', 'tuberculosis', 'allergies', 'underCareOfPhysician',
            'illnessComplication', 'isPregnant', 'menstrualPeriodRegular', 'additionalHealthInfo',
            'clenchingGrindingTeeth', 'lipSuckingBiting', 'mouthBreather', 'musicalInstrument',
            'nailBiting', 'speechProblems', 'nursingBottleHabits', 'thumbFingerSucking',
            'tongueThrusting', 'MedicalAlert', 'createdAt', 'updatedAt', 'patientID', '__v',
            'extraTeeth', 'isArchived'
        ];

        // Prepare CSV rows
        const csvRows = [
            headers, // Add header row
            ...selectedPatients.map(record =>
                headers.map(field => {
                    let value = record[field];

                    // Handle null or undefined values
                    if (value == null) return '';

                    // Explicit handling for boolean values
                    if (typeof value === 'boolean') {
                        return value ? 'true' : 'false';
                    }

                    // Escape values containing special characters (commas, quotes, etc.)
                    if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                        value = `"${value.replace(/"/g, '""')}"`; // Escape double quotes by doubling them
                    }

                    return value;
                })
            ),
        ];

        // Convert rows to CSV format
        const csvContent = csvRows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);

        // Trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = 'PatientRecords.csv';
        link.click();
        URL.revokeObjectURL(url); // Clean up the URL object
    };

    const handleDownloadTemplate = () => {
        // Define headers for the CSV file
        const headers = [
            '_id', 'dateOfExam', 'chartNumber', 'name', 'sex', 'birthdate', 'ageYear', 'ageMonth',
            'guardianName', 'occupation', 'address', 'ContactInformation', 'email', 'chiefComplaint',
            'medicalDentalHistory', 'orthodonticHistory', 'currentTreatment', 'congenitalAbnormalities',
            'tmjProblems', 'oralHygiene', 'gingivalTissues', 'abnormalBleeding', 'asthma', 'cancer',
            'congenitalHeartDefect', 'convulsionsEpilepsy', 'diabetes', 'handicapsDisabilities',
            'hearingImpairment', 'heartMurmur', 'hemophilia', 'hepatitis', 'hivAids', 'kidneyLiverProblems',
            'rheumaticScarletFever', 'tuberculosis', 'allergies', 'underCareOfPhysician',
            'illnessComplication', 'isPregnant', 'menstrualPeriodRegular', 'additionalHealthInfo',
            'clenchingGrindingTeeth', 'lipSuckingBiting', 'mouthBreather', 'musicalInstrument',
            'nailBiting', 'speechProblems', 'nursingBottleHabits', 'thumbFingerSucking',
            'tongueThrusting', 'MedicalAlert', 'createdAt', 'updatedAt', 'patientID', '__v',
            'extraTeeth', 'isArchived'
        ];

        // Prepare CSV rows
        const csvRows = [
            headers, // Add header row
            ...selectedPatients.map(record =>
                headers.map(field => {
                    let value = ''
                    return value;
                })
            ),
        ];

        // Convert rows to CSV format
        const csvContent = csvRows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);

        // Trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = 'PatientRecords.csv';
        link.click();
        URL.revokeObjectURL(url); // Clean up the URL object
    };

    const filteredData = patients
        .filter(patient => patient.name && patient.name.toLowerCase().includes(searchText.toLowerCase()))
        .filter(patient => {
            // Filter based on viewOption
            if (viewOption === 'archived') {
                return patient.isArchived === true; // Show only archived patients
            }
            return patient.isArchived !== true; // Default: show only non-archived patients
        });

    // Reset filter logic when search is cleared
    const handleSearchClear = () => {
        setSearchText(""); // Clear search text
        setViewOption("all"); // Reset viewOption to 'all'
    };

    const paginatedData = filteredData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    const handleRowClick = (row) => {
        navigate(`/patient-profile/${row._id}`);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = date.getFullYear().toString().slice(-2);

        return `${month < 10 ? '0' + month : month}/${day < 10 ? '0' + day : day}/${year}`;
    };

    const columns = [
        { name: "Patient ID", selector: row => row.patientID, sortable: true, filterable: true },
        { name: "Name", selector: row => row.name, sortable: true, filterable: true },
        { name: "Sex", selector: row => row.sex, sortable: true, filterable: true },
        { name: "Age", selector: row => row.ageYear, sortable: true, filterable: true },
        {
            name: "Date Created",
            selector: row => formatDate(row.createdAt),
            sortable: true,
            filterable: true,
            cell: row => format(new Date(row.updatedAt), 'MM/dd/yyyy hh:mm a')
        },
        {
            name: "Date Updated",
            selector: row => formatDate(row.updatedAt),
            sortable: true,
            filterable: true,
            cell: row => format(new Date(row.updatedAt), 'MM/dd/yyyy hh:mm a')
        },
    ];

    const archivePatient = async (id) => {
        try {
            const response = await fetch(`https://api.hgcdental.clinic/api/patients/archive/${id}`, {
                method: 'PATCH',
            });

            if (response.ok) {
                alert('Patient archived successfully');
                setPatients((prevPatients) => prevPatients.filter(patient => patient._id !== id));
            } else {
                const errorData = await response.json();
                alert(`Failed to archive patient: ${errorData.message}`);
            }
        } catch (error) {
            console.error('Error archiving patient:', error);
            alert('An unexpected error occurred while archiving the patient');
        }
    };

    const customStyles = {
        rows: {
            style: {
                borderBottom: '1px solid #ddd',
            },
        },
        headCells: {
            style: {
                backgroundColor: '#007bff',
                color: 'white',
                fontWeight: 'bold',
                padding: '15px 10px',
                textAlign: 'center',
            },
        },
        cells: {
            style: {
                padding: '12px 10px',
                borderRight: '1px solid #ddd',
            },
        },
        pagination: {
            style: {
                display: 'flex',
                justifyContent: 'center',
                padding: '10px',
                backgroundColor: '#f4f4f4',
            },
        },
        sortIcon: {
            style: {
                color: '#007bff',
                fontSize: '18px',
            },
        },
    };

    // Event listener to close dropdown when clicked outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Close the dropdown if the click is outside the dropdown container
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);  // Close the dropdown
            }
        };

        // Add event listener
        document.addEventListener("click", handleClickOutside);

        // Clean up event listener
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    return (
        <AccessControl>
            <div className={`data-table-page ${isSidebarVisible ? 'sidebar-visible' : 'sidebar-hidden'}`}>
                <InternalHeader moduleName="Patients" toggleSidebar={toggleSidebar} />
                <div className="data-table-container">
                    <Sidebar isSidebarVisible={isSidebarVisible} />
                    <div className="data-table-container2">
                        <div className="data-table-header">
                            <div className="flex-div">
                                <a href="/create-patient">
                                    <button className="create-button">New</button>
                                </a>
                                <Actions
                                    onImportClick={() => {
                                        document.getElementById('file-input').click();
                                    }}
                                    onDownloadTemplateClick={() => {
                                        handleDownloadTemplate();
                                    }}
                                />
                                <input
                                    id="file-input"
                                    type="file"
                                    accept=".csv"
                                    style={{ display: 'none' }}
                                    onChange={handleImport}
                                />
                            </div>
                            <div>
                                {/* Conditionally render Filter or Actions dropdown */}
                                {selectedRows.length > 0 ? (
                                    <div className="actions-dropdown-container">
                                        <button onClick={handleActionsToggle} className="actions-button">
                                            <FaCog />  Actions
                                        </button>
                                        {isActionsDropdownOpen && (
                                            <div className="actions-dropdown-options1">
                                                <div onClick={handleExport}>
                                                    <FaUpload /> Export Records
                                                </div>
                                                <div onClick={handleDeleteSelected}>
                                                    <FaArchive /> Archive
                                                </div>

                                                {/* Conditionally render the Unarchive button */}
                                                {viewOption === 'archived' && (
                                                    <div onClick={handleUnDeleteSelected}>
                                                        <FaArchive /> Unarchive
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <Filter
                                        searchText={searchText}
                                        setSearchText={setSearchText}
                                        viewOption={viewOption}
                                        setViewOption={setViewOption}
                                        onClear={handleSearchClear} // Add a prop for clearing the search
                                    />
                                )}
                            </div>
                            <div className="column-div">
                                {!isKanbanView && (
                                    <CustomPagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        rowsPerPage={rowsPerPage}
                                        onPageChange={handlePageChange}
                                        onRowsPerPageChange={handleRowsPerPageChange}
                                        totalRecords={filteredData.length}
                                    />
                                )}
                                <button
                                    onClick={() => {
                                        const newKanbanView = !isKanbanView;
                                        setIsKanbanView(newKanbanView);
                                        setViewType(newKanbanView ? 'kanban' : 'table');
                                    }}
                                    className="view-toggle-button"
                                >
                                    {viewType === 'table' ? <FaTh size={20} /> : <FaThList size={20} />}
                                </button>
                            </div>
                        </div>
                        <div>
                            {isKanbanView ? (
                                <div className="table-form-kanban">
                                    <KanbanView patients={filteredData} onRowClick={handleRowClick} />
                                </div>
                            ) : (
                                <div className="table-form">
                                    <div>
                                        <DataTable
                                            title="Patients"
                                            columns={columns}
                                            data={paginatedData}
                                            highlightOnHover
                                            selectableRows
                                            selectableRowsHighlight
                                            onRowClicked={handleRowClick}
                                            onSelectedRowsChange={handleSelectedRowsChange}
                                            striped
                                            customStyles={customStyles}
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="log-display-section">
                                <h2>Logs</h2>
                                <div className="log-container">
                                    <ul>
                                        {logs.map((log, index) => (
                                            <li key={index}>{log.timestamp} - {log.action}: {log.details}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AccessControl>
    );
};

export default Patients;